/**
 * Tagging Compliance Page - 4 Tabs
 * Tab 1: Tagging Compliance (non-compliant resources with filters, view-only
 *        tickets - raising them per-resource lives on Remediation now)
 * Tab 2: Remediation (untagged resources grouped into account x tag-key
 *        scopes, one ticket each - see tag-remediation.service.ts)
 * Tab 3: Tagging Policy (required tags)
 * Tab 4: Cost Allocation Tags
 *
 * Chargeback was removed from this page. It still exists on the Invoice page,
 * which is where a department-wise cost breakdown belongs - this page is about
 * tag hygiene, and mixing a finance report into it made the page do two
 * unrelated jobs. The /tagging/chargeback endpoint is intentionally left in
 * place because InvoicePage still consumes it.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Tag, ShieldCheck, CheckCircle2, AlertTriangle, Layers, Wrench } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { getActiveTenantId } from '../services/tenantContext';
import { Button } from '../components/common/Button';
import { Badge, BadgeVariant } from '../components/common/Badge';
import { StatCard } from '../components/common/StatCard';
import { Card } from '../components/common/Card';
import { PillTabs } from '../components/common/Modal';
import { TagRemediationTab } from '../components/tagging/TagRemediationTab';

const COST_API_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : 'http://localhost:3002/api/v1';

// tenantContextMiddleware requires a verified Bearer token now - tenant
// headers alone are no longer sufficient, so this must read the current
// token fresh on every call rather than being a static object.
// forceFresh adds the standard Cache-Control: no-cache, which the cost API
// honours by skipping its read cache (see shouldBypassCache). Cost responses
// are cached server-side for an hour, so without this the Refresh button
// re-fetched and got the identical cached payload - it looked broken because
// functionally it was.
function getHeaders(forceFresh = false): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    'x-user-id': 'user-123',
    ...(forceFresh ? { 'Cache-Control': 'no-cache' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const TaggingCompliancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'compliance' | 'remediation' | 'policy' | 'allocation'>('compliance');
  const [summary, setSummary] = useState<any>(null);
  const [nonCompliant, setNonCompliant] = useState<any[]>([]);
  const [costAllocationTags, setCostAllocationTags] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { canEditCostAllocationTags } = usePermissions();

  // Filters for Tab 1
  const [filterProvider, setFilterProvider] = useState('all');
  const [filterRegion, setFilterRegion] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [filterTag, setFilterTag] = useState('all');

  // State for Cost Allocation Tags editing
  const [newTagKey, setNewTagKey] = useState('');
  const [newTagProvider, setNewTagProvider] = useState('aws');
  const [savingTags, setSavingTags] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async (forceFresh = false) => {
    try {
      if (forceFresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const h = getHeaders(forceFresh);
      const [compRes, nonCompRes, allocRes] = await Promise.all([
        axios.get(`${COST_API_URL}/tagging/compliance`, { headers: h, timeout: 60000 }),
        axios.get(`${COST_API_URL}/tagging/non-compliant?limit=500`, { headers: h, timeout: 60000 }),
        axios.get(`${COST_API_URL}/tagging/cost-allocation-tags`, { headers: h, timeout: 10000 }).catch(() => ({ data: { data: null } })),
      ]);
      setSummary(compRes.data.data);
      setNonCompliant(nonCompRes.data.data || []);
      setCostAllocationTags(allocRes.data.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Get unique values for filter dropdowns — each dropdown's options cascade
  // from every filter selected above it (provider -> region -> service), so
  // picking a region actually narrows the service list instead of leaving
  // stale options that lead to an always-empty result.
  const providerFilteredResources = filterProvider === 'all'
    ? nonCompliant
    : nonCompliant.filter(r => r.provider === filterProvider);
  const regions = [...new Set(providerFilteredResources.map(r => r.region))].filter(Boolean).sort();
  const regionFilteredResources = filterRegion === 'all'
    ? providerFilteredResources
    : providerFilteredResources.filter(r => r.region === filterRegion);
  const services = [...new Set(regionFilteredResources.map(r => r.resourceType))].filter(Boolean).sort();
  // Read the required-tag list from the API rather than keeping a second
  // hardcoded copy here - the two lists had already drifted apart in practice,
  // and the backend list is now per-policy so a local copy can only be wrong.
  const requiredTags: string[] = (summary?.requiredTags || []).map((t: any) =>
    typeof t === 'string' ? t : t.key
  );

  // Format service/resource type to friendly display name
  const formatServiceName = (service: string): string => {
    if (!service) return service;
    // Azure: microsoft.compute/virtualmachines â†’ Virtual Machines
    if (service.startsWith('microsoft.')) {
      const parts = service.split('/');
      const name = parts[parts.length - 1];
      return name.replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, s => s.toUpperCase())
        .replace(/virtualmachines/i, 'Virtual Machines')
        .replace(/storageaccounts/i, 'Storage Accounts')
        .replace(/virtualnetworks/i, 'Virtual Networks')
        .replace(/publicipaddresses/i, 'Public IP Addresses')
        .replace(/loadbalancers/i, 'Load Balancers')
        .replace(/containerapps/i, 'Container Apps')
        .replace(/flexibleservers/i, 'Flexible Servers')
        .replace(/virtualmachinescalesets/i, 'VM Scale Sets')
        .replace(/applicationgateways/i, 'Application Gateways')
        .replace(/cognitiveservices/i, 'Cognitive Services')
        .replace(/containerregistry/i, 'Container Registry')
        .replace(/disks/i, 'Disks')
        .replace(/sites/i, 'Web Apps')
        .replace(/vaults/i, 'Key Vaults')
        .replace(/namespaces/i, 'Namespaces')
        .replace(/servers/i, 'Servers')
        .replace(/databases/i, 'Databases')
        .replace(/registries/i, 'Registries');
    }
    // GCP: compute.googleapis.com/Instance â†’ Compute Instance
    if (service.includes('googleapis.com/')) {
      const parts = service.split('/');
      const resource = parts[parts.length - 1];
      const svcPart = parts[0].split('.')[0];
      const svcName = svcPart.charAt(0).toUpperCase() + svcPart.slice(1);
      return `${svcName} ${resource}`;
    }
    // AWS: already short names like ec2, s3, lambda
    return service.charAt(0).toUpperCase() + service.slice(1);
  };

  // Format resource ID to a readable name
  const formatResourceName = (resourceId: string, provider: string): string => {
    if (!resourceId) return 'Unknown';
    
    if (provider === 'aws') {
      // AWS ARN: arn:aws:service:region:account:resource-type/resource-name
      // or arn:aws:service:region:account:resource-type:resource-name
      const parts = resourceId.split(':');
      if (parts.length >= 6) {
        const resourcePart = parts.slice(5).join(':');
        // Get the last meaningful segment after / or :
        const segments = resourcePart.split('/');
        const lastSegment = segments[segments.length - 1];
        // If there's a type/name pattern, show "type/name"
        if (segments.length >= 2) {
          return `${segments[segments.length - 2]}/${lastSegment}`;
        }
        return lastSegment || resourcePart;
      }
      return resourceId.split('/').pop() || resourceId;
    }
    
    if (provider === 'azure') {
      // Azure: /subscriptions/.../providers/Microsoft.Compute/virtualMachines/vm-name
      const parts = resourceId.split('/');
      // Get last 2 segments (type/name)
      if (parts.length >= 2) {
        return parts[parts.length - 1];
      }
      return resourceId;
    }
    
    if (provider === 'gcp') {
      // GCP: //service.googleapis.com/projects/project/zones/zone/instances/name
      const parts = resourceId.split('/');
      return parts[parts.length - 1] || resourceId;
    }
    
    return resourceId.split('/').pop() || resourceId;
  };

  // Multi-account fan-out (see tagging-compliance.service.ts's
  // scanAwsTagsForTenant) means resources here can come from several
  // different AWS accounts / Azure subscriptions / GCP projects at once -
  // without this, there's no way to tell which onboarded account a given
  // non-compliant resource actually belongs to.
  const extractAccountId = (resourceId: string, provider: string, accountId?: string): string => {
    // The backend now reports the owning account directly (from the account
    // whose role produced the scan), which is the only reliable source for
    // global services - S3/Route53/IAM ARNs carry an empty account segment,
    // so the ARN parsing below can't recover it. Parsing stays as a fallback
    // for rows from older responses / mock data.
    if (accountId) return accountId;

    if (!resourceId) return '-';

    if (provider === 'aws') {
      // arn:aws:service:region:account-id:resource...
      const parts = resourceId.split(':');
      return parts.length >= 5 && parts[4] ? parts[4] : '-';
    }

    if (provider === 'azure') {
      // /subscriptions/<subscription-id>/...
      const match = resourceId.match(/\/subscriptions\/([^/]+)/);
      return match ? match[1] : '-';
    }

    if (provider === 'gcp') {
      // //service.googleapis.com/projects/<project-id>/...
      const match = resourceId.match(/\/projects\/([^/]+)/);
      return match ? match[1] : '-';
    }

    return '-';
  };

  // Apply filters
  const filteredResources = nonCompliant.filter(r => {
    if (filterProvider !== 'all' && r.provider !== filterProvider) return false;
    if (filterRegion !== 'all' && r.region !== filterRegion) return false;
    if (filterService !== 'all' && r.resourceType !== filterService) return false;
    if (filterTag !== 'all' && !r.nonCompliantKeys?.includes(filterTag)) return false;
    return true;
  });

  const getScoreColor = (score: number) => score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-rose-600';

  if (loading) return (
    <div className="flex justify-center items-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary/30 mx-auto"></div>
    </div>
  );

  const providerStatusVariant = (status: string): BadgeVariant => (status === 'active' ? 'success' : status === 'pending' ? 'warning' : 'muted');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end gap-2">
        <Button onClick={() => fetchData(true)} disabled={refreshing} size="sm">
          <RefreshCw className={refreshing ? "animate-spin" : undefined} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 dark:bg-rose-900/20 dark:border-rose-700 dark:text-rose-300 px-4 py-2.5 text-sm rounded-lg">{error}</div>}

      {/* Tabs: Tagging Compliance -> Remediation -> Tagging Policy -> Cost Allocation */}
      <PillTabs
        tabs={[
          // Policy first: it is the rule, and compliance/remediation are
          // measurements against it. Reading the score before the standard it
          // is scored against is backwards.
          { key: 'policy', label: 'Tagging Policy', icon: ShieldCheck },
          { key: 'compliance', label: 'Tagging Compliance', icon: Tag },
          { key: 'remediation', label: 'Remediation', icon: Wrench },
          { key: 'allocation', label: 'Cost Allocation Tags', icon: Layers },
        ]}
        active={activeTab}
        onChange={(k) => setActiveTab(k as typeof activeTab)}
      />

      {activeTab === 'remediation' && (
        <TagRemediationTab
          apiBase={COST_API_URL}
          authHeaders={getHeaders}
          providerFilter={filterProvider}
        />
      )}

      {/* TAB 1: Tagging Compliance */}
      {activeTab === 'compliance' && summary && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <StatCard label="Overall Compliance" value={`${summary.overall.complianceScore}%`} icon={ShieldCheck} accent={summary.overall.complianceScore >= 80 ? 'success' : summary.overall.complianceScore >= 50 ? 'warning' : 'danger'} />
            <StatCard label="Compliant Resources" value={summary.overall.compliantResources} icon={CheckCircle2} accent="success" />
            <StatCard label="Non-Compliant Resources" value={summary.overall.nonCompliantResources} icon={AlertTriangle} accent="danger" />
            <StatCard label="Total Resources" value={summary.overall.totalResources} icon={Layers} accent="primary" />
          </div>

          {/* Provider Cards */}
          <Card className="p-4">
            <h3 className="text-sm font-semibold mb-3 text-foreground">Compliance by Cloud Provider</h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(summary.byProvider).map(([provider, data]: [string, any]) => (
                <div key={provider} className="border border-border rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold uppercase text-sm text-foreground">{provider}</span>
                    <Badge variant={providerStatusVariant(data.status)}>{data.status.toUpperCase()}</Badge>
                  </div>
                  {data.status === 'active' && <p className={`text-xl font-bold mt-2 ${getScoreColor(data.complianceScore)}`}>{data.complianceScore}%</p>}
                  {data.status !== 'active' && <p className="text-xs text-muted-foreground mt-2">{data.status === 'pending' ? 'Integration pending' : 'Not configured'}</p>}
                </div>
              ))}
            </div>
          </Card>

          {/* Drill Down by Tag Key */}
          <div className="bg-card rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-foreground">Drill Down by Tag Key</h3>
            <p className="text-sm text-muted-foreground mb-4">Click a tag to see all resources missing that specific tag</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {requiredTags.map(tag => {
                const count = nonCompliant.filter(r => r.nonCompliantKeys?.includes(tag)).length;
                const isSelected = filterTag === tag;
                return (
                  <button
                    key={tag}
                    onClick={() => setFilterTag(isSelected ? 'all' : tag)}
                    className={`rounded-lg border p-4 text-left transition-all hover:shadow-md ${isSelected ? 'border-primary/30 bg-primary/10 ring-2 ring-primary/30 dark:bg-primary/10 dark:ring-primary/30' : 'border-border hover:border-primary/30 dark:border-border dark:hover:border-primary/30'}`}
                  >
                    <p className="text-xs font-medium text-muted-foreground uppercase">{tag}</p>
                    <p className={`text-2xl font-bold mt-1 ${count > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{count}</p>
                    <p className="text-xs text-muted-foreground mt-1">{count > 0 ? 'resources missing' : 'all compliant'}</p>
                    {isSelected && <p className="text-xs text-primary mt-1 font-medium">âœ“ Filtered</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card rounded-lg shadow p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Cloud Provider</label>
                <select value={filterProvider} onChange={e => { setFilterProvider(e.target.value); setFilterRegion('all'); setFilterService('all'); }} className="w-full px-3 py-2 border rounded-lg text-sm dark:border-border dark:bg-secondary dark:text-white">
                  <option value="all">All Providers</option>
                  <option value="aws">AWS</option>
                  <option value="azure">Azure</option>
                  <option value="gcp">GCP</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Region</label>
                <select value={filterRegion} onChange={e => { setFilterRegion(e.target.value); setFilterService('all'); }} className="w-full px-3 py-2 border rounded-lg text-sm dark:border-border dark:bg-secondary dark:text-white">
                  <option value="all">All Regions</option>
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Service</label>
                <select value={filterService} onChange={e => setFilterService(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm dark:border-border dark:bg-secondary dark:text-white">
                  <option value="all">All Services</option>
                  {services.map(s => <option key={s} value={s}>{formatServiceName(s)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground/80 mb-1">Missing Tag</label>
                <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm dark:border-border dark:bg-secondary dark:text-white">
                  <option value="all">All Tags</option>
                  {requiredTags.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <span className="text-sm text-muted-foreground">{filteredResources.length} results</span>
              </div>
            </div>
          </div>

          {/* Non-Compliant Table */}
          <div className="bg-card rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Resource</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Region</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Missing Tag Keys</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredResources.slice(0, 50).map((r, i) => (
                    <tr key={i} className="hover:bg-muted/30 dark:hover:bg-secondary/50">
                      <td className="px-4 py-3 text-sm max-w-xs truncate text-foreground" title={r.resourceId}>
                        {formatResourceName(r.resourceId, r.provider)}
                      </td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs bg-secondary dark:text-foreground rounded uppercase">{r.provider}</span></td>
                      <td className="px-4 py-3 text-sm font-mono text-foreground/80">{extractAccountId(r.resourceId, r.provider, r.accountId)}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{r.region}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{formatServiceName(r.resourceType)}</td>
                      {/* Partial progress - so correctly adding some (but not
                          all) required tags is immediately visible, instead of
                          the row looking identical until the very last key. */}
                      <td className="px-4 py-3">
                        {(() => {
                          const total = requiredTags.length || 1;
                          const missing = (r.nonCompliantKeys || '').split(',').map((s: string) => s.trim()).filter(Boolean).length;
                          const present = Math.max(0, total - missing);
                          const pct = Math.round((present / total) * 100);
                          return (
                            <div className="flex items-center gap-2" title={`${present} of ${total} required tags present`}>
                              <div className="h-1.5 w-16 rounded-full bg-secondary">
                                <div
                                  className={`h-1.5 rounded-full ${pct >= 67 ? 'bg-emerald-500' : pct >= 34 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs tabular-nums text-muted-foreground">{present}/{total}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(r.nonCompliantKeys || '').split(',').map((tag: string) => {
                            const trimmed = tag.trim();
                            if (!trimmed) return null;
                            return (
                              <button
                                key={trimmed}
                                onClick={() => setFilterTag(filterTag === trimmed ? 'all' : trimmed)}
                                className={`px-2 py-0.5 text-xs rounded cursor-pointer transition-colors ${filterTag === trimmed ? 'bg-primary text-white' : 'bg-rose-500/10 text-rose-600 hover:bg-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:hover:bg-rose-900/50'}`}
                                title={`Click to filter by "${trimmed}"`}
                              >
                                {trimmed}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredResources.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No resources match the selected filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Compliant Resources */}
          {summary.compliantResourcesList && summary.compliantResourcesList.length > 0 && (
            <div className="bg-card rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">âœ… Compliant Resources ({summary.compliantResourcesList.length})</h3>
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Resource</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Region</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {summary.compliantResourcesList.map((r: any, i: number) => (
                    <tr key={i} className="hover:bg-emerald-50 dark:hover:bg-emerald-900/10">
                      <td className="px-4 py-3 text-sm max-w-xs truncate text-foreground" title={r.resourceId}>
                        {formatResourceName(r.resourceId, r.provider)}
                      </td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300 rounded uppercase">{r.provider}</span></td>
                      <td className="px-4 py-3 text-sm font-mono text-foreground/80">{extractAccountId(r.resourceId, r.provider, r.accountId)}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{r.region}</td>
                      <td className="px-4 py-3 text-sm text-foreground/80">{r.service}</td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300 rounded">All tags present âœ“</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: Tagging Policy */}
      {activeTab === 'policy' && summary && (
        <div className="bg-card rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 text-foreground">Required Tags Policy</h3>
          <p className="text-sm text-muted-foreground mb-4">All cloud resources must have the following tags for proper cost allocation and governance.</p>
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tag Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Allowed Values</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Applies To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Enforcement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {summary.requiredTags.map((tag: any) => (
                <tr key={tag.key}>
                  <td className="px-6 py-4 font-medium text-foreground">{tag.key}</td>
                  <td className="px-6 py-4">
                    {tag.allowedValues ? (
                      <div className="flex flex-wrap gap-1">{tag.allowedValues.map((v: string) => <span key={v} className="px-2 py-0.5 text-xs bg-sky-500/10 text-sky-600 dark:bg-primary/10 dark:text-primary rounded">{v}</span>)}</div>
                    ) : <span className="text-muted-foreground text-sm">{tag.description || 'Any value'}</span>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-1">{tag.providers.map((p: string) => <span key={p} className="px-2 py-0.5 text-xs bg-secondary dark:text-foreground rounded uppercase">{p}</span>)}</div>
                  </td>
                  <td className="px-6 py-4"><span className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300 rounded">Mandatory</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* TAB 3: Cost Allocation Tags */}
      {activeTab === 'allocation' && (
        <div className="space-y-6">
          <div className="bg-card rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2 text-foreground">Cost Allocation Tags</h3>
            <p className="text-sm text-muted-foreground mb-1">
              Cost allocation tags are used for generating cost reports grouped by business dimensions.
              Activate a tag here to include it in Cost &amp; Usage Reports.
            </p>
            <p className="text-xs text-muted-foreground/70 mb-6">
              These tags allow you to break down costs by business unit, environment, application, or any custom dimension. Only active tags appear in cost reports and can be used in Cost Explorer for grouping.
            </p>

            {costAllocationTags ? (
              <>
                {Object.entries(costAllocationTags).map(([provider, data]: [string, any]) => (
                  <div key={provider} className="mb-8">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-foreground uppercase">
                        {provider} <span className="text-xs text-muted-foreground normal-case font-normal">({data.accountId || data.subscriptionId || data.projectId})</span>
                      </h4>
                      <span className="text-xs text-muted-foreground">{data.costAllocationTags.filter((t: any) => t.status === 'Active').length} active / {data.costAllocationTags.length} total</span>
                    </div>
                    <div className="divide-y divide-border dark:divide-border border border-border rounded-lg overflow-hidden">
                      {data.costAllocationTags.map((tag: any, i: number) => (
                        <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 dark:hover:bg-secondary/50">
                          <span className="text-sm font-medium text-foreground">{tag.tagKey}</span>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => {
                                if (!canEditCostAllocationTags) return;
                                const updated = { ...costAllocationTags };
                                updated[provider].costAllocationTags[i].status = tag.status === 'Active' ? 'Inactive' : 'Active';
                                setCostAllocationTags({ ...updated });
                              }}
                              disabled={!canEditCostAllocationTags}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${tag.status === 'Active' ? 'bg-emerald-500' : 'bg-secondary dark:bg-secondary'} ${!canEditCostAllocationTags ? 'opacity-50 cursor-not-allowed' : ''}`}
                              aria-label={`Toggle ${tag.tagKey} ${tag.status === 'Active' ? 'off' : 'on'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-card shadow transition-transform ${tag.status === 'Active' ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                            <span className={`text-xs font-medium w-14 ${tag.status === 'Active' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                              {tag.status}
                            </span>
                            {canEditCostAllocationTags && (
                            <button
                              onClick={() => {
                                const updated = { ...costAllocationTags };
                                updated[provider].costAllocationTags = updated[provider].costAllocationTags.filter((_: any, idx: number) => idx !== i);
                                setCostAllocationTags({ ...updated });
                              }}
                              className="text-muted-foreground/70 hover:text-rose-600 dark:text-muted-foreground dark:hover:text-rose-400 text-sm"
                              title="Remove tag"
                            >
                              âœ•
                            </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Add new tag - Admin only */}
                {canEditCostAllocationTags && (
                <div className="border-t border-border pt-6 mt-6">
                  <h4 className="text-sm font-semibold text-foreground mb-3">Add New Cost Allocation Tag</h4>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-foreground/80 mb-1">Tag Key</label>
                      <input
                        type="text"
                        value={newTagKey}
                        onChange={e => setNewTagKey(e.target.value)}
                        placeholder="e.g., BusinessUnit"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:border-ring focus:ring-1 focus:ring-ring focus:outline-none dark:border-border dark:bg-secondary dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground/80 mb-1">Provider</label>
                      <select
                        value={newTagProvider}
                        onChange={e => setNewTagProvider(e.target.value)}
                        className="px-3 py-2 border rounded-lg text-sm dark:border-border dark:bg-secondary dark:text-white"
                      >
                        <option value="aws">AWS</option>
                        <option value="azure">Azure</option>
                        <option value="gcp">GCP</option>
                      </select>
                    </div>
                    <button
                      onClick={() => {
                        if (!newTagKey.trim()) return;
                        const updated = { ...costAllocationTags };
                        if (updated[newTagProvider]) {
                          updated[newTagProvider].costAllocationTags.push({
                            tagKey: newTagKey.trim(),
                            type: 'User',
                            status: 'Active',
                            lastUpdated: new Date().toISOString().split('T')[0],
                          });
                          setCostAllocationTags({ ...updated });
                          setNewTagKey('');
                        }
                      }}
                      disabled={!newTagKey.trim()}
                      className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Tag
                    </button>
                  </div>
                </div>
                )}

                {/* Save button - Admin only */}
                {canEditCostAllocationTags && (
                <div className="border-t border-border pt-4 mt-6 flex justify-end">
                  <button
                    onClick={async () => {
                      try {
                        setSavingTags(true);
                        setSaveMessage(null);
                        console.log('Saving cost allocation tags:', costAllocationTags);
                        const response = await axios.put(`${COST_API_URL}/tagging/cost-allocation-tags`, costAllocationTags, { headers: getHeaders() });
                        console.log('Save response:', response);
                        setSaveMessage({type: 'success', text: 'Cost allocation tags saved successfully!'});
                        setTimeout(() => setSaveMessage(null), 3000);
                      } catch (e: any) {
                        console.error('Error saving cost allocation tags:', e);
                        setSaveMessage({type: 'error', text: `Failed to save: ${e.message || 'Unknown error'}`});
                      } finally {
                        setSavingTags(false);
                      }
                    }}
                    disabled={savingTags}
                    className="px-6 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {savingTags ? 'Saving...' : 'Save Changes'}
                  </button>
                  {saveMessage && (
                    <div className={`ml-4 px-4 py-2 rounded-lg text-sm ${saveMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-rose-500/10 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300'}`}>
                      {saveMessage.text}
                    </div>
                  )}
                </div>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">Cost allocation tags data not available.</p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export { TaggingCompliancePage };
